import { Outlet } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useCallback, useState } from 'react';
import Sidebar from './Sidebar.jsx';
import Header from './Header.jsx';
import { useRealtime } from '../../hooks/useRealtime.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useAgentStore } from '../../store/agentStore.jsx';
import { useAppVersion } from '../../hooks/useAppVersion.js';
import { api } from '../../lib/api.js';
import { mergeCallEvent } from '../../lib/calls.js';

export default function AppShell() {
  const { stale: staleBuild } = useAppVersion();
  const { impersonating, endImpersonation, user } = useAuth();
  const { agentId, setAgentId } = useAgentStore();
  // The ORG feed: one socket, every host. `agentId` still drives which host the
  // Overview's per-host panels show — it no longer decides what the live feed
  // carries, which is what made calls on a second PBX invisible.
  const { connected, connectionState, agentStatus, hostStatus, lastEvent } = useRealtime();
  const queryClient = useQueryClient();

  // Debounce query invalidation to coalesce rapid WebSocket events.
  const pendingInvalidations = useRef(new Set());
  const debounceTimer = useRef(null);

  const flushInvalidations = useCallback(() => {
    for (const key of pendingInvalidations.current) {
      queryClient.invalidateQueries({ queryKey: JSON.parse(key) });
    }
    pendingInvalidations.current.clear();
  }, [queryClient]);

  const scheduleInvalidation = useCallback((...queryKey) => {
    pendingInvalidations.current.add(JSON.stringify(queryKey));
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(flushInvalidations, 300);
  }, [flushInvalidations]);

  useEffect(() => {
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, []);

  // Live-calls state. Keyed by the call's linkedid (falling back to
  // uniqueid) so the same call across state transitions maps to one
  // row. 'ended' removes the entry; other states upsert. Passed down
  // to any page that wants to render a "who's on the phone right now"
  // strip via useOutletContext.
  const [activeCalls, setActiveCalls] = useState({});   // { key: { key, state, from, to, ext, ts } }

  // Sweep entries older than 5 minutes — protects against events that
  // never receive their 'ended' partner (agent restart mid-call, WS
  // gap, etc.). Cheap since the map is tiny.
  useEffect(() => {
    const t = setInterval(() => {
      setActiveCalls((prev) => {
        const cutoff = Date.now() - 5 * 60_000;
        let changed = false;
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          if (v.ts > cutoff) next[k] = v;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  // Dispatch real-time events → TanStack Query invalidation + live-call
  // state. Event types must stay in sync with
  // cloud/workers/src/durable/AgentRelay.js VALID_EVENT_TYPES.
  useEffect(() => {
    if (!lastEvent) return;

    switch (lastEvent.type) {
      case 'registration.up':
      case 'registration.down':
        scheduleInvalidation('sip-accounts', agentId);
        break;

      case 'call.state_change': {
        scheduleInvalidation('calls-active', agentId);
        // Also track in local state so the Overview's Live Calls widget
        // has something to render. Tolerant to schema drift — agent may
        // send { linkedid, uniqueid, state, from, to, ext, ts } or minor
        // variants; we key on whichever id is present.
        // The merge lives in lib/calls.js so it can be replayed against the captured
        // event stream instead of only against a real ringing phone. See mergeCallEvent.
        setActiveCalls((prev) => mergeCallEvent(prev, lastEvent));
        break;
      }

      case 'cdr.written':
        scheduleInvalidation('cdr', agentId);
        break;

      case 'agent.status':
        scheduleInvalidation('agents-list');
        break;
    }
  }, [lastEvent, agentId, scheduleInvalidation]);

  // Subscribe to agents-list so the offline banner re-renders when an agent
  // flips from pending → active. TanStack Query dedupes by key — Header.jsx's
  // query stays the single fetch.
  const { data: agents } = useQuery({
    queryKey: ['agents-list'],
    queryFn: () => api.get('/agents'),
  });

  // Default the active agent to the first one (and recover if the current
  // selection disappears). This drives the live-call WebSocket. The top-bar
  // agent selector was removed — the Overview's host tabs now set the agent —
  // so this global default lives here instead of in the Header.
  useEffect(() => {
    if (agents === undefined) return;
    const list = Array.isArray(agents) ? agents : [];
    const exists = list.some((a) => a.agent_id === agentId);
    if (agentId && !exists) setAgentId(list[0]?.agent_id ?? null);
    else if (!agentId && list.length > 0) setAgentId(list[0].agent_id);
  }, [agentId, agents, setAgentId]);

  const selectedAgent = Array.isArray(agents) ? agents.find((a) => a.agent_id === agentId) : null;
  // On the org feed the selected host's state comes from the per-host map;
  // `agentStatus` only carries a value in single-agent mode.
  const selectedHostStatus = (agentId && hostStatus[agentId]) || agentStatus;
  const agentPending = selectedAgent?.status === 'pending' && selectedHostStatus !== 'online';
  // The browser WebSocket to the Durable Object connects even for pending
  // agents (the DO accepts the browser before the upstream is live), so
  // `!connected` alone would hide the banner for the exact case it's meant
  // to surface. Show it whenever the agent is still pending too.
  const showOfflineBanner = agentId && (!connected || agentPending);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* The refactor freeze. Shown before anything else and above the
            impersonation banner, because it changes what every save on every page
            actually does: Verso records the change and no phone system hears it.
            An operator who learns this from a save that reports "nothing
            happened" has already been misled once. */}
        {user?.write_freeze?.frozen && (
          <div className="flex items-center gap-2 bg-slate-700 px-4 py-2 text-sm font-medium text-white">
            <span className="inline-block h-2 w-2 rounded-full bg-amber-300" />
            <span>
              <strong>Changes save in Verso only.</strong> While the line-model
              refactor is in progress Verso will not update the phone system, the carrier or
              GDMS, so nothing here reaches a live phone. Steps that would have are
              reported as <em>blocked</em> rather than done.
            </span>
          </div>
        )}
        {impersonating && (
          <div className="flex items-center justify-between bg-amber-500 px-4 py-2 text-sm font-medium text-white">
            <span>Impersonating <strong>{impersonating.org_name || impersonating.org_id}</strong> as org_admin</span>
            <button
              onClick={async () => {
                await endImpersonation();
                // This tab was opened via handoff from the admin console, so
                // close it. If the browser blocks the close, endImpersonation
                // has already restored the verso_admin session which triggers
                // the AuthGate "use admin console" screen.
                window.close();
              }}
              className="rounded-md border border-white/50 bg-white/10 px-3 py-1 text-xs font-semibold text-white hover:bg-white/20"
            >
              End Session
            </button>
          </div>
        )}
        <Header />
        {showOfflineBanner && (
          <div className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white ${agentPending ? 'bg-blue-500' : 'bg-red-500'}`}>
            <span className="inline-block h-2 w-2 rounded-full bg-white opacity-75" />
            {agentPending
              ? 'Waiting for agent to connect. Complete the agent setup and start the verso-agent service to begin managing this PBX.'
              : connectionState === 'connecting'
                ? 'Reconnecting to agent...'
                : 'Agent connection lost. Data may be stale. Reconnecting...'}
          </div>
        )}
        <main className="flex-1 overflow-y-auto p-6">
          {/* A tab left open across a deploy keeps its old bundle, so the operator
              sees a version of the product that no longer exists. That produced
              three "this feature is missing" reports on 2026-07-29 against
              features that had already shipped. */}
          {staleBuild && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5">
              <span className="text-sm text-amber-900">
                A newer version of Verso is available. This tab is still running the
                build it loaded, so anything added since then won&rsquo;t appear here.
              </span>
              <button
                onClick={() => window.location.reload()}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Reload
              </button>
            </div>
          )}
          <Outlet context={{ activeCalls, hostStatus }} />
        </main>
        <footer className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-2 text-xs text-gray-400">
          <span>Verso &copy; 2026 Colorado Communications</span>
          <span className="font-mono text-gray-600" title="Deployed build">
            v{typeof __VERSO_VERSION__ !== 'undefined' ? __VERSO_VERSION__ : 'dev'}
          </span>
        </footer>
      </div>
    </div>
  );
}
